import React from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import {
  useListNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from '../../../../../api/notificationsApi';
import type { Notification } from '@nicsmith/shared_types';
import { formatMessagePreview } from '../../../../../utils/format-message-preview';
import styles from './NotificationDropdown.module.scss';

const cx = classNames.bind(styles);

interface NotificationDropdownProps {
  onClose: () => void;
}

const TYPE_ICONS: Record<string, { icon: string; className: string }> = {
  new_message: { icon: 'fas fa-comment-dots', className: 'message' },
  new_course: { icon: 'fas fa-book', className: 'course' },
  new_module: { icon: 'fas fa-layer-group', className: 'module' },
  new_review: { icon: 'fas fa-star', className: 'system' },
  system: { icon: 'fas fa-bullhorn', className: 'system' },
};

function timeAgo(dateString: string, t: (key: string, fallback: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return t('notifications.justNow', 'Just now');
  if (diffMin < 60) return t('notifications.minutesAgo', '{{count}}m ago', { count: diffMin });
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return t('notifications.hoursAgo', '{{count}}h ago', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return t('notifications.daysAgo', '{{count}}d ago', { count: diffDays });
  return new Date(dateString).toLocaleDateString();
}

// Map known server notification titles/messages to i18n keys
const TITLE_MAP: Record<string, string> = {
  "New course available!": "notifications.content.newCourseAvailable",
  "New module available!": "notifications.content.newModuleAvailable",
  "New message from admin": "notifications.content.newMessageFromAdmin",
  "New message from curator": "notifications.content.newMessageFromCurator",
};

const TITLE_PATTERNS: Array<{ re: RegExp; key: string; group: string }> = [
  { re: /^New message from (.+)$/, key: "notifications.content.newMessageFrom", group: "name" },
  { re: /^Bug report from (.+)$/, key: "notifications.content.bugReportFrom", group: "name" },
  { re: /^Your bug report was (.+)$/, key: "notifications.content.bugReportStatus", group: "status" },
  { re: /^New review: (.+)$/, key: "notifications.content.newReview", group: "course" },
];

const MSG_PATTERN = /^"(.+)" is now available\.$/;
const RATED_PATTERN = /^(.+) rated (\d)\/5(?:: "(.+)")?$/;

function translateNotifTitle(title: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const exact = TITLE_MAP[title];
  if (exact) return t(exact) || title;
  for (const { re, key, group } of TITLE_PATTERNS) {
    const m = title.match(re);
    if (m) return t(key, { [group]: m[1] }) || title;
  }
  return title;
}

function translateNotifMessage(message: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const m = message.match(MSG_PATTERN);
  if (m) return t("notifications.content.isNowAvailable", { name: m[1] }) || message;
  const r = message.match(RATED_PATTERN);
  if (r) {
    const base = t("notifications.content.userRated", { name: r[1], rating: r[2] });
    if (r[3]) return `${base}: "${r[3]}"`;
    return base || message;
  }
  return message;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data } = useListNotificationsQuery({ limit: 20 });
  const [markRead] = useMarkNotificationReadMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();

  const notifications = data?.items ?? [];

  const handleClick = (notif: Notification & { SK?: string }) => {
    if (!notif.is_read && notif.SK) {
      markRead({ notifId: notif.notification_id, sk: notif.SK });
    }
    if (notif.link) {
      navigate(notif.link);
    }
    onClose();
  };

  const handleMarkAllRead = () => {
    markAllRead();
  };

  return (
    <div className={cx('dropdown')}>
      <div className={cx('header')}>
        <span className={cx('headerTitle')}>
          {t('notifications.title', 'Notifications')}
        </span>
        {notifications.some((n) => !n.is_read) && (
          <button className={cx('markAllBtn')} onClick={handleMarkAllRead}>
            {t('notifications.markAllRead', 'Mark all read')}
          </button>
        )}
      </div>

      <div className={cx('list')}>
        {notifications.length === 0 ? (
          <div className={cx('empty')}>
            {t('notifications.empty', 'No notifications yet')}
          </div>
        ) : (
          notifications.map((notif: Notification & { SK?: string }) => {
            const typeInfo = TYPE_ICONS[notif.type] || TYPE_ICONS.system;
            return (
              <button
                key={notif.notification_id}
                className={cx('item', { unread: !notif.is_read })}
                onClick={() => handleClick(notif)}
              >
                <div className={cx('typeIcon', typeInfo.className)}>
                  <i className={typeInfo.icon}></i>
                </div>
                <div className={cx('content')}>
                  <p className={cx('title')}>{translateNotifTitle(notif.title, t)}</p>
                  <p className={cx('message')}>{formatMessagePreview(translateNotifMessage(notif.message, t))}</p>
                  <span className={cx('time')}>{timeAgo(notif.created_at, t)}</span>
                </div>
                {!notif.is_read && <div className={cx('unreadDot')} />}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
